<template>
  <v-dialog v-model="dialog" max-width="800px" persistent>
    <v-card>
      <v-card-title class="headline">
        <v-icon color="warning" left>mdi-clock-alert</v-icon>
        {{ dialogTitle }}
      </v-card-title>

      <v-card-text v-if="runningTasks.length===0">
        <p class="mb-3">
          No tasks are running.  <span v-if="showContinue">Click continue to start the new task.</span>
        </p>
      </v-card-text>
      <v-card-text v-if="runningTasks.length>0">
        <p class="mb-3">The following {{ runningTasks.length }} tasks are currently running. Please indicate if they are
          done or paused.</p>

        <v-card
          v-for="task in runningTasks"
          :key="task.id"
          class="mb-4"
          outlined
        >
          <v-card-title class="subtitle-1 font-weight-medium py-2">
            {{ task.title }}
          </v-card-title>
          <v-card-subtitle v-if="task.description" class="pb-1">
            {{ task.description }}
          </v-card-subtitle>
          <v-card-text class="pb-2">
            <div class="d-flex align-center flex-wrap">
              <span v-if="getTeamMemberNames(task)" class="mr-4">
                <v-icon class="mr-1" small>mdi-account</v-icon>
                {{ getTeamMemberNames(task) }}
              </span>
              <span v-if="getTaskStartTime(task)" class="mr-4">
                <v-icon class="mr-1" small>mdi-clock-start</v-icon>
                Started: {{ getTaskStartTime(task) }}
              </span>
              <span v-if="getTaskElapsedTime(task)">
                <v-icon :color="getDurationWarningColor(task)" class="mr-1" small>{{
                    getDurationWarningIcon(task)
                  }}</v-icon>
                <span :class="getDurationWarningClass(task)">
                  Duration: {{ getTaskElapsedTime(task) }}
                  <span v-if="getTaskEstimate(task)">
                    (estimated {{ getTaskEstimate(task) }})
                    <v-icon v-if="isTaskOverEstimate(task)" :color="getDurationWarningColor(task)" class="ml-1" small>
                      {{ isTaskCriticallyOver(task) ? 'mdi-alert-circle' : 'mdi-alert' }}
                    </v-icon>
                  </span>
                  <span v-else-if="!getTaskEstimate(task)" class="grey--text">
                    (no estimate available)
                  </span>
                </span>
              </span>
            </div>
            <div v-if="getTaskEstimate(task)" class="mt-2">
              <v-progress-linear
                :color="getProgressBarColor(task)"
                :value="getTaskProgressPercentage(task)"
                class="mb-1"
                height="6"
                rounded
              />
              <div :class="getProgressTextClass(task)" class="caption text-center">
                {{ getProgressText(task) }}
              </div>
            </div>
          </v-card-text>
          <v-divider/>
          <v-card-actions>
            <v-btn
              v-if="showShort"
              color="pink"
              outlined
              @click="enterShort(task)"
            >
              Short
            </v-btn>
            <v-spacer/>
            <v-btn
              :loading="task.loading"
              color="warning"
              outlined
              @click="pauseTask(task)"
              class="mr-2"
            >
              Pause
            </v-btn>
            <v-btn
              :loading="task.loading"
              color="success"
              outlined
              @click="markTaskComplete(task)"
            >
              Done
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-card-text>

      <v-card-actions>
        <v-btn color="grey" v-if="showContinue" text @click="cancelTaskStart">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="showContinue && runningTasks.length===0" color="primary" @click="continueWithNewTask">
          Continue
        </v-btn>
        <v-btn v-if="showContinue && runningTasks.length>0" color="warning" @click="continueWithNewTask">
          Ignore And Continue
        </v-btn>
        <v-btn v-if="!showContinue" color="primary" @click="continueWithNewTask">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getTeamMemberNames,
  getTaskStartTime,
  getTaskElapsedTime,
  getTaskElapsedMinutes,
  getTaskEstimate,
  getTaskEstimateMinutes,
  isTaskOverEstimate,
  isTaskCriticallyOver,
  getDurationWarningColor,
  getDurationWarningIcon,
  getDurationWarningClass,
  getTaskProgressPercentage,
  getProgressBarColor,
  getProgressText,
  getProgressTextClass
} from '@/utils/tasks/task-common';

export default {
  name: 'RunningTasksDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    runningTasks: {
      type: Array,
      default: () => []
    },
    showContinue: { type: Boolean, default: false },
    showShort: { type: Boolean, default: false },
  },
  data() {
    return {
      currentTime: new Date()
    }
  },

  mounted() {
    setTimeout(() => this.currentTime = new Date(), 1000)
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    dialogTitle() {
      if (this.runningTasks.length === 0) {
        return 'Running Tasks';
      }

      // Collect all unique team members from running tasks
      const teamMembersSet = new Set();
      this.runningTasks.forEach(task => {
        if (task.execution && task.execution.length > 0) {
          const lastExecution = task.execution[task.execution.length - 1];
          if (lastExecution.team_members) {
            lastExecution.team_members.forEach(tm => {
              teamMembersSet.add(tm.name);
            });
          }
        }
      });

      const teamMemberNames = Array.from(teamMembersSet).sort();

      if (teamMemberNames.length === 0) {
        return 'Running Tasks';
      }

      return `Running Tasks for ${teamMemberNames.join(', ')}`;
    }
  },
  methods: {
    // Import utility functions directly
    getTeamMemberNames,
    getTaskStartTime,
    getTaskElapsedTime,
    getTaskElapsedMinutes,
    getTaskEstimate,
    getTaskEstimateMinutes,
    isTaskOverEstimate,
    isTaskCriticallyOver,
    getDurationWarningColor,
    getDurationWarningIcon,
    getDurationWarningClass,
    getTaskProgressPercentage,
    getProgressBarColor,
    getProgressText,
    getProgressTextClass,
    markTaskComplete(task) {
      console.log('Marking task complete:' + task.id, task);
      this.$set(task, 'loading', true);
      const id = task.id;
      this.$socket.emit('task:stop', {id, complete: true}, (result) => {
        console.log('task:stop - result', result);
        this.$set(task, 'loading', false);
      });
    },
    pauseTask(task) {
      console.log('Pausing task:', task);
      this.$set(task, 'loading', true);
      const id = task.id;
      this.$socket.emit('task:stop', {id, complete: false}, (result) => {
        console.log('task:stop - result', result);
        this.$set(task, 'loading', false);
      });
    },
    cancel() {
      this.dialog = false;
    },
    enterShort(task) {
      this.dialog = false;
      this.$emit('short', task);
    },
    continueWithNewTask() {
      this.dialog = false;
      this.$emit('continue-with-new-task');
    },
    cancelTaskStart() {
      this.dialog = false;
      this.$emit('cancel-task-start');
    }
  }
}
</script>

<style scoped>
.headline {
  word-break: normal;
}
</style>